import { FormatMessageType } from '@hooks/useValidation';
import { END, NUMBER, POSITIVE, REQUIRED } from '@utils/validation/validation.constant';
import Yup from '@utils/validation/yup-extended';
import { isAfter } from 'date-fns';

import { DroneClearanceEnum } from '@/types/utm/drone.types';
import { AbstractFlightPlan } from '@/types/utm/flightPlan.types';

import { polygonSchema } from './polygon.schema';

export function droneSchemaValidation(formatMessage: FormatMessageType) {
  const strategicDroneSchema = Yup.object({
    name: Yup.string().required(formatMessage({ id: REQUIRED })),
    weight: Yup.number()
      .nullable()
      .typeError(
        formatMessage({ id: NUMBER }, { field: formatMessage({ id: 'components.utm.drones.tab.public.weight' }) }),
      )
      .min(0, formatMessage({ id: POSITIVE })),
    autonomy: Yup.number()
      .nullable()
      .typeError(
        formatMessage({ id: NUMBER }, { field: formatMessage({ id: 'components.utm.drones.tab.public.autonomy' }) }),
      )
      .min(0, formatMessage({ id: POSITIVE })),
    range: Yup.number()
      .nullable()
      .typeError(
        formatMessage({ id: NUMBER }, { field: formatMessage({ id: 'components.utm.drones.tab.public.range' }) }),
      )
      .min(0, formatMessage({ id: POSITIVE })),
    safetyDistance: Yup.number()
      .required(formatMessage({ id: REQUIRED }))
      .min(0, formatMessage({ id: POSITIVE })),
    criticalDistance: Yup.number()
      .required(formatMessage({ id: REQUIRED }))
      .min(0, formatMessage({ id: POSITIVE }))
      .when(['safetyDistance'], ([safetyDistance], schema) => {
        if (safetyDistance !== null) {
          return schema.test(
            'less-than-safety',
            formatMessage({ id: 'components.utm.drones.validation.critical' }),
            (value) => value < safetyDistance,
          );
        }
        return schema;
      }),
    strategicClearance: Yup.mixed<DroneClearanceEnum>()
      .oneOf(Object.values(DroneClearanceEnum))
      .required(formatMessage({ id: REQUIRED })),
  });

  return Yup.object({
    serialNumber: Yup.string().required(formatMessage({ id: REQUIRED })),
    strategicDrone: strategicDroneSchema,
  });
}

export function pilotSchemaValidation(formatMessage: FormatMessageType) {
  return Yup.object({
    firstName: Yup.string().required(formatMessage({ id: REQUIRED })),
    lastName: Yup.string().required(formatMessage({ id: REQUIRED })),
    phoneNumber: Yup.string().required(formatMessage({ id: REQUIRED })),
    email: Yup.string()
      .validEmail()
      .required(formatMessage({ id: REQUIRED })),
  });
}

export function fleetSchemaValidation(formatMessage: FormatMessageType) {
  return Yup.object({
    name: Yup.string().required(formatMessage({ id: REQUIRED })),
    strategicClearance: Yup.mixed<DroneClearanceEnum>()
      .oneOf(Object.values(DroneClearanceEnum))
      .required(formatMessage({ id: REQUIRED })),
    chips: Yup.array().of(
      Yup.array()
        .length(2)
        .of(Yup.mixed())
        .test(
          'chip-label-not-empty',
          formatMessage({ id: REQUIRED }),
          (value) => !!value && typeof value[0] === 'string' && value[0].trim() !== '',
        ),
    ),
  });
}

export function flightPlanSchemaValidation(formatMessage: FormatMessageType) {
  return Yup.object<Partial<AbstractFlightPlan>>({
    name: Yup.string().required(formatMessage({ id: REQUIRED })),
    startTime: Yup.date().required(formatMessage({ id: REQUIRED })),
    endTime: Yup.date()
      .required(formatMessage({ id: REQUIRED }))
      .when(['startTime'], ([startTime], schema) => {
        if (startTime !== null) {
          return schema.test('more-than-startTime', formatMessage({ id: END }), (value) => isAfter(value, startTime));
        }
        return schema;
      }),
    drones: Yup.array()
      .required(formatMessage({ id: REQUIRED }))
      .min(1, formatMessage({ id: 'components.utm.drones.validation.drones' })),
    heightMin: Yup.number()
      .nullable()
      .min(0, formatMessage({ id: POSITIVE })),
    heightMax: Yup.number()
      .nullable()
      .when(['heightMin'], ([heightMin], schema) => {
        if (heightMin !== null && heightMin !== undefined) {
          return schema.test(
            'more-than-heightMin',
            formatMessage({ id: 'form.min' }, { value: heightMin }),
            (value) => value === null || value === undefined || value > heightMin,
          );
        }
        return schema;
      }),
    polygons: polygonSchema(formatMessage),
  });
}
